import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabManager } from '../context/TabManagerContext';
import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';
import { isWorkspaceRoute } from '../utils/resolveWorkspaceEntryPath';
import { KeepAlivePane } from './KeepAlivePane';
import { getRedirectForPath, shouldKeepAliveRoute } from './resolveRouteEntry';
import { setKeepAliveActiveNavigationPath } from './keepAliveNetworkGate';

interface TabKeepAliveOutletProps {
  notFoundElement: React.ReactNode;
}

const buildVisitedSet = (path: string): Set<string> => {
  const initial = new Set<string>();
  if (isWorkspaceRoute(path)) {
    initial.add(path);
  }
  return initial;
};

export const TabKeepAliveOutlet: React.FC<TabKeepAliveOutletProps> = ({ notFoundElement }) => {
  const { tabs } = useTabManager();
  const location = useLocation();
  const navigate = useNavigate();
  const activePath = location.pathname;

  useLayoutEffect(() => {
    if (!isTabKeepAliveEnabled()) {
      setKeepAliveActiveNavigationPath(null);
      return;
    }
    setKeepAliveActiveNavigationPath(
      isWorkspaceRoute(activePath) ? activePath : null,
    );
  }, [activePath]);

  const [visitedPaths, setVisitedPaths] = useState<Set<string>>(() => buildVisitedSet(activePath));

  useEffect(() => {
    if (!isTabKeepAliveEnabled()) return;

    const redirectTo = getRedirectForPath(activePath);
    if (redirectTo && redirectTo !== activePath) {
      navigate(redirectTo, { replace: true });
    }
  }, [activePath, navigate]);

  useEffect(() => {
    if (!isTabKeepAliveEnabled() || !isWorkspaceRoute(activePath)) return;

    setVisitedPaths((prev) => {
      if (prev.has(activePath)) return prev;
      const next = new Set(prev);
      next.add(activePath);
      return next;
    });
  }, [activePath]);

  const alivePaths = useMemo(() => {
    const paths = new Set<string>();

    if (isWorkspaceRoute(activePath)) {
      paths.add(activePath);
    }

    for (const tab of tabs) {
      if (!isWorkspaceRoute(tab.path)) continue;
      if (shouldKeepAliveRoute(tab.path)) {
        paths.add(tab.path);
      }
    }

    return paths;
  }, [tabs, activePath]);

  const mountedPaths = useMemo(() => {
    const aliveSet = new Set(alivePaths);
    return Array.from(visitedPaths).filter((path) => aliveSet.has(path));
  }, [alivePaths, visitedPaths]);

  useEffect(() => {
    const aliveSet = new Set(alivePaths);
    setVisitedPaths((prev) => {
      const pruned = Array.from(prev).filter((path) => aliveSet.has(path));
      if (pruned.length === prev.size) return prev;
      return new Set(pruned);
    });
  }, [alivePaths]);

  if (!isTabKeepAliveEnabled()) {
    return null;
  }

  return (
    <>
      {mountedPaths.map((path) => (
        <KeepAlivePane
          key={`${path}:${tabs.find((tab) => tab.path === path)?.refreshKey ?? 0}`}
          path={path}
          isActive={path === activePath}
          notFoundElement={notFoundElement}
        />
      ))}
    </>
  );
};
